import { useEffect, useId, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { Modal as ModalProps } from "./index.types";
import "./index.css";
import Button from "../button";
import clsx from "clsx";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function Modal({
  className,
  modalref,
  children,
  title,
  subtitle,
  footer,
  hasOverlay = true,
  hasCloseButton = true,
  handleClose,
  ...rest
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const handleCloseRef = useRef(handleClose);
  const titleId = useId();
  const subtitleId = useId();

  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  const setRefs = (node: HTMLDivElement | null) => {
    dialogRef.current = node;

    if (typeof modalref === "function") {
      modalref(node);
    } else if (modalref) {
      (modalref as React.MutableRefObject<HTMLDivElement | null>).current =
        node;
    }
  };

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const node = dialogRef.current;
    const focusable = node?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

    if (focusable && focusable.length > 0) {
      focusable[0].focus();
    } else {
      node?.focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseRef.current?.();
        return;
      }

      if (event.key === "Tab" && node) {
        const focusableEls = Array.from(
          node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );

        if (focusableEls.length === 0) {
          event.preventDefault();
          return;
        }

        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  return (
    <>
      {hasOverlay && (
        <div
          className="modal-overlay"
          onClick={() => handleClose?.()}
        />
      )}
      <div
        ref={setRefs}
        className={clsx("modal", className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
        {...rest}
      >
        {hasCloseButton && (
          <Button
            mode="secondary"
            className="modal__close"
            size="small"
            onClick={handleClose}
            aria-label="Close"
          >
            <IoClose />
          </Button>
        )}
        <div className="modal__content">
          {title && (
            <div className="modal__header">
              <h3 id={titleId}>{title}</h3>
              <h6 id={subtitleId}>{subtitle}</h6>
            </div>
          )}
          <div className="modal__children">{children}</div>
          {footer && <div className="modal__footer">{footer}</div>}
        </div>
      </div>
    </>
  );
}

export default Modal;
